/**
 * @Author: Zero
 * @Date: 2025-05-15 09:59:22
 * @Explain: 分组级联面板
 */

import { intlGet } from '@/locales';
import classNames from 'classnames';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox, List } from 'tdesign-react';
import { v4 as uuidv4 } from 'uuid';
// components
import { Icon } from '@/components/tdesign';
// type
import { CommonOptionsType } from '@/definition/common/data.d';
import { CascaderOptionListProps } from '../data.d';
// utils
import {
  areArraysContentEqual,
  getAllPathsFromRoot,
} from '@/utils/common/dataJudgment';
import { addBy, compareNum, minusBy, percentage } from '@/utils/format/number';
import { defaultListMaxHeight } from '../const';

type PropsType = {
  /** 列表宽度 */
  listWidth?: number | string;
} & CascaderOptionListProps;
const CascaderGroupList = (props: PropsType) => {
  const {
    searchable,
    searchPlaceholder,
    allOption,
    multiple,
    options,
    value,
    displayTextKey,
    emptyText = intlGet('COMMON.NO.DATA'),
    className,
    valueMode,
    valueType,
    showAllLevels,
    listWidth,
    groupKey = 'group',
    listMaxHeight = defaultListMaxHeight,
    listHeader,
    onScroll,
    onClose,
    onClick,
    onChange,
  } = props;
  const onText = (item: any) => intlGet(item.labelKey) || item.label || '';

  let defaultList = [{ parentId: 0, leavel: 0, list: options }];
  // 级联面板展示数据，每一个元素即为一个面板内容
  const [renderListData, setRenderListData] =
    useState<{ parentId: number | string; leavel: number; list: any[] }[]>(
      defaultList,
    );

  /** 重组参数 */
  const getItem = (item: CommonOptionsType) => {
    const text: any = onText(item);
    const tips: any = intlGet(item.tipsKey) || item.tips || '';
    const textRender =
      (displayTextKey && (item as any)[displayTextKey]) || text;

    return {
      text,
      tips,
      textRender,
    };
  };

  // 点击一行触发，如果有子数据，则展开
  const handleExpand = ({
    currentItem,
    leavel,
  }: {
    currentItem: any;
    leavel: number;
  }) => {
    // 无子集不可展开
    if (!currentItem?.children?.length) return;

    // 是否存在展示中子层级
    let onShow = renderListData.find(
      (v) => v.parentId && v.parentId === currentItem.value,
    );

    if (!onShow) {
      // 查询当前点击项层级
      let clickSibling = renderListData.find((v) =>
        v.list.some((h) => h.value === currentItem.value),
      ) || { leavel: 0 };

      let newRenderListData = renderListData.filter(
        (v) => v.leavel <= clickSibling?.leavel,
      );

      setRenderListData([
        ...newRenderListData,
        {
          parentId: currentItem.value,
          leavel: +addBy(leavel, 1),
          list: currentItem.children,
        },
      ]);
    }
  };

  // 点击checkbox，面板展示关联上下级所有内容
  const handleExpandCheckbox = ({
    currentItem,
    currentList,
  }: {
    currentItem: any;
    currentList: any[];
  }) => {
    let parrentIds = currentItem?.parentLinkId || [];

    // 触发项即为父级 & 无子级：值展示一层父级面板
    if (!parrentIds.length && !currentItem?.children?.length) {
      setRenderListData([...defaultList]);
      return;
    }

    // 触发项即为父级 & 有子级：值展示一层父级面板
    if (!parrentIds.length && currentItem?.children?.length) {
      let childrenList = {
        parentId: currentItem.value,
        leavel: 1,
        list: currentItem.children || [],
      };
      setRenderListData([...defaultList, childrenList]);
      return;
    }

    // 触发项非父级
    let parrentLists: any[] = [];
    let opts = [...options];
    let lastParentId =
      currentItem?.parentLinkId?.[currentItem?.parentLinkId?.length - 1] || 0;

    // 触发项上级面板信息查找
    for (let i = 0; i < currentItem?.parentLinkId?.length; i++) {
      let valueId = currentItem.parentLinkId[i];
      let parentId = i === 0 ? 0 : currentItem.parentLinkId[i - 1];
      const findParentList = (opts: any[]) => {
        opts.map((v: any) => {
          if (v.value === valueId) {
            parrentLists.push({ parentId, leavel: i, list: opts });
            v?.children?.length && (opts = v.children);
            return;
          }
        });
      };
      findParentList(opts);
    }
    // 当前触发项所处层级信息加入展示面板中
    parrentLists.push({
      parentId: lastParentId,
      leavel: currentItem?.parentLinkId?.length,
      list: currentList,
    });
    // 当前触发项带有子级，将第一层子级加入展示面板中
    if (currentItem?.children?.length) {
      parrentLists.push({
        parentId: currentItem.value,
        leavel: addBy(currentItem?.parentLinkId?.length, 1),
        list: currentItem.children || [],
      });
    }
    setRenderListData([...parrentLists]);
  };

  // 获取指定项涉及的上下游所有链路信息
  const getCurrentItemAllLinkId = ({ currentItem }: any) => {
    // 当前链路所有ID（包含父级和子级）
    let allLinkId: any[][] = [];
    // 当前项父级链路ID
    let parentLinkID = [...currentItem.parentLinkId];
    // 当前项ID
    parentLinkID.push(currentItem.value);
    if (currentItem?.children?.length) {
      let childrenAllPathsFromRoot = [];
      // 将树形结构数据处理为从当前到分支顶端链路各自处理为各自一组数据
      if (currentItem?.children?.length) {
        childrenAllPathsFromRoot = getAllPathsFromRoot({
          tree: currentItem.children,
        });
      }
      // 当前链路子级所有ID
      childrenAllPathsFromRoot.map((h) => {
        let childrenLinkId = h.map((k: any) => k?.value);
        let allId = [...parentLinkID, ...childrenLinkId];
        allLinkId.push(allId);
      });
    } else {
      allLinkId.push(parentLinkID);
    }
    return allLinkId;
  };
  // 限制选中结果数据结构
  // [
  //   ['1', '1.1', '1.1.6666'],
  //   ['1', '1.1', '1.1.62222'],
  //   ['1', '1.3'],
  // ]
  // 准备工作：将树形结构数据处理为从根到分支顶端链路各自处理为各自一组数据
  // 左侧改变checkbox，导致右侧已选变化
  // 存在情况一（假设当前节点为 n，父节点即上一层节点为 p，子集节点m）：
  // 1. 改变其选中状态，不会改变其父节点的选中状态（第一级没有父节点）
  // 2. 改变其选中状态，会改变其父节点的选中状态：
  //    2.1 选中，设置所有子孙节点m为 **选中状态**，向上寻找，
  //        2.1.1 由于选中导致其 **父节点 p** 也被选中
  //        2.1.2 由于选中导致其 **父节点 p** 也被选中，同时父节点的其他兄弟节点都被选中的，那么继续向上寻找，
  //              直到出现 2.1.1 情况 或 到达第一级（最顶层）
  //    2.2 取消选中，设置其子节点m为 **未选中状态** 向上寻找，
  //        2.2.1 由于取消选中导致其父节点也取消选中，父节点状态改为半选状态
  //        2.2.2 由于取消选中导致其父节点也取消选中，同时其父节点兄弟节点存在未被选中的，那么继续向上寻找，
  //              直到出现 2.2.1 情况 或 到达第一级（最顶层）
  // 手动更新选中状态
  const handleChange = useCallback(
    ({ allLinkId, status }: any) => {
      // 选中当前项
      if (status) {
        onChange?.([...value, ...allLinkId], {});
      }

      // 取消选中当前项
      if (!status) {
        let newResult: any[][] = [];
        value.map((h: any) => {
          let isCancel = allLinkId.some((k: any) =>
            areArraysContentEqual({
              arr1: h,
              arr2: k,
            }),
          );
          if (!isCancel) newResult.push(h);
        });
        onChange?.(newResult, {});
      }
    },
    [value, options, getCurrentItemAllLinkId],
  );

  // 获取选框状态
  const getCheckboxStatus = useCallback(
    ({ currentItem, allLinkId }: any) => {
      // 选中状态
      let isChecked = false;
      // 半选状态
      let idIndeterminate = false;

      let checkedArr: boolean[] = [];
      allLinkId.map((h: any) => {
        let hasUnChecked = value.some((k: any) => {
          let isSame = areArraysContentEqual({ arr1: h, arr2: k });
          if (currentItem?.children?.length && isSame) idIndeterminate = true;
          return isSame;
        });
        checkedArr.push(hasUnChecked);
      });
      isChecked = !!(checkedArr.length && checkedArr.every((h) => h));
      if (isChecked) idIndeterminate = false;
      return {
        isChecked,
        idIndeterminate,
      };
    },
    [value, options, handleChange, getCurrentItemAllLinkId],
  );

  const getGroup = (data: any[], key: string) => {
    let arr: {
      groupName: any;
      groupUuid: string;
      groupList: any[];
    }[] = [];
    data.forEach((item) => {
      let groupName = item[key];
      let index = arr.findIndex((v) => v.groupName === groupName);
      if (index === -1) {
        arr.push({
          groupName,
          groupUuid: uuidv4(),
          groupList: [item],
        });
      } else {
        arr[index].groupList.push(item);
      }
    });

    return arr;
  };

  // 级联面板列宽度
  const listWidthValue = useMemo(() => {
    if (listWidth) {
      return listWidth;
    }
    let length = renderListData.length;
    if (!length || length === 1) {
      return '100%';
    }
    let percent = percentage({ molecule: length, denominator: 100 });
    return `${percent}%`;
  }, [listWidth, length]);

  // 收集当前渲染级联选项头部&list类名
  const forstListHeader: string[] = [];
  const renderText = useCallback(
    (list: any[]) => {
      let result = [];
      for (let i = 0; i < list.length; i++) {
        let newObj = list[i];
        let _forstListHeader: string = uuidv4();
        forstListHeader.push(_forstListHeader);
        const groupArr = getGroup(newObj?.list || [], groupKey);
        result.push(
          <div className="td-scroll-list-item" key={`td-scroll-list-item-${i}`}>
            {/* 列表 - 头部 */}
            <div className={_forstListHeader}>
              {listHeader?.({ item: newObj })}
            </div>
            <List
              key={i}
              style={{ width: listWidthValue, maxHeight: listMaxHeight }}
              className={classNames(
                'td-scroll-list-cascader',
                `${_forstListHeader}-${i}`,
              )}
            >
              {groupArr.map((v, index) => (
                <div key={index}>
                  <li key={v.groupUuid} className="t-list-item-group">
                    {v.groupName}
                  </li>
                  {(v?.groupList || [])?.map((item: any) => {
                    const { text, tips, textRender } = getItem(item);
                    const commonProps = {
                      key: item.value,
                      title: tips ? tips : text,
                    };
                    // 当前链路所有ID（包含父级和子级）
                    let allLinkId: any[][] = getCurrentItemAllLinkId({
                      currentItem: item,
                    });
                    // 查找选中信息中是否存在当前链路ID信息
                    let isChecked = getCheckboxStatus({
                      currentItem: item,
                      allLinkId,
                    })?.isChecked;
                    const isExpanded =
                      item.value === list?.[addBy(i, 1)]?.parentId;
                    return (
                      <li
                        className={classNames('t-list-item', {
                          'is-disabled': item?.disabled,
                          'is-expanded': isExpanded,
                        })}
                        {...commonProps}
                        onClick={() => {
                          if (item?.disabled) {
                            return;
                          }
                          // 目的使得组件重新渲染，重新定位弹层位置
                          // unUpdateValue 非更新数据标识
                          onChange?.([...value], { unUpdateValue: true });
                          if (!item?.children?.length) {
                            handleChange({
                              allLinkId,
                              status: !isChecked,
                            });
                          }
                        }}
                      >
                        <div className="t-list-item-main">
                          <Checkbox
                            checked={isChecked}
                            disabled={item?.disabled}
                            indeterminate={
                              getCheckboxStatus({
                                currentItem: item,
                                allLinkId,
                              })?.idIndeterminate
                            }
                            onChange={(val) => {
                              item?.children?.length &&
                                handleChange({
                                  allLinkId,
                                  status: val,
                                });
                            }}
                            onClick={() =>
                              handleExpandCheckbox({
                                currentItem: item,
                                currentList: newObj?.list || [],
                              })
                            }
                          />
                          <div
                            className="t-list-item-content"
                            onClick={() =>
                              handleExpand({ currentItem: item, leavel: i })
                            }
                          >
                            <div className="t-list-item-content-text">
                              {textRender}
                            </div>
                            {item?.children?.length ? (
                              <Icon.Font name="arrow-right font-size-24 color-333" />
                            ) : (
                              ''
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </div>
              ))}
            </List>
          </div>,
        );
      }

      return result;
    },
    [
      value,
      renderListData,
      listWidthValue,
      listMaxHeight,
      handleExpand,
      handleExpandCheckbox,
      handleChange,
      getCheckboxStatus,
      getCurrentItemAllLinkId,
    ],
  );

  useEffect(() => {
    if (listHeader) {
      // 获取当前级联渲染的header&list类名及header高度
      const allRenderListHeight = [];
      // 获取渲染的级联列表中listHeader高度最大的值
      let headerMaxHeight = 0;
      for (let i = 0; i < forstListHeader.length; i++) {
        const headerClassName = forstListHeader[i];
        const listClassName = `${headerClassName}-${i}`;
        const listDom = document.getElementsByClassName(listClassName);
        let headerHeight = (listDom[0] as any)?.offsetTop || 0;
        // 60为筛选框占用高度
        headerHeight = headerHeight > 60 ? minusBy(headerHeight, 60) : 0;

        if (compareNum(headerHeight, headerMaxHeight) === 1) {
          headerMaxHeight = headerHeight;
        }
        allRenderListHeight.push({
          headerClassName,
          listClassName,
          headerHeight,
        });
      }

      for (let i = 0; i < allRenderListHeight.length; i++) {
        const { listClassName, headerHeight } = allRenderListHeight[i];
        const listDom = document.getElementsByClassName(listClassName);

        listDom.length &&
          Array.from(listDom).forEach((el: any) => {
            // list设置最大高度
            if (el) {
              // 规定list列表最大高度 listMaxHeight
              let maxHeight: number | string =
                minusBy(addBy(listMaxHeight, headerMaxHeight), headerHeight) +
                'px';
              el.style.maxHeight = maxHeight;
            }
          });
      }
    }
  }, [listHeader, listMaxHeight]);
  return multiple ? (
    <div className={classNames('flex-block', className)}>
      {renderText(renderListData)}
    </div>
  ) : (
    '单选暂未开发'
  );
};

export default CascaderGroupList;
